import { createSelector } from '@reduxjs/toolkit';
import { select_device_capabilities } from 'console/redux/selectors';
import { asyncGenerateTrips, filterGPSPoints, ghostLocation } from './index';
import { postJob } from 'console/worker/calculator';
import { JobType } from 'console/worker/types';
import { any2unixtime } from 'webui/utils/time';
import { selectDevice } from 'entities/device/lib';
import { findDeviceTrack } from '../model/reducer';
export function selectDeviceTrack(state, device_id) {
    if (device_id == null) {
        return undefined;
    }
    return findDeviceTrack(state.tracking, device_id);
}
export function select_raw_data(state, device_id) {
    return selectDeviceTrack(state, device_id)?.raw_data ?? null;
}
export function select_mintime(state, device_id) {
    return selectDeviceTrack(state, device_id)?.mintime;
}
export function select_maxtime(state, device_id) {
    return selectDeviceTrack(state, device_id)?.maxtime;
}
export function select_confirmed_viewport(state, device_id) {
    return selectDeviceTrack(state, device_id)?.confirmed_viewport;
}
export function select_viewport(state, device_id) {
    return selectDeviceTrack(state, device_id)?.viewport;
}
export function select_loading(state, device_id) {
    return selectDeviceTrack(state, device_id)?.loading;
}
export function select_error(state, device_id) {
    return selectDeviceTrack(state, device_id)?.error;
}
export function select_hover_points_on_map(state, device_id) {
    return selectDeviceTrack(state, device_id)?.hover_points_on_map;
}
export function select_selected_point_on_map(state, device_id) {
    return selectDeviceTrack(state, device_id)?.selected_point_on_map;
}
export const async_select_track_and_icons = createSelector((state, device_id) => device_id, select_device_capabilities, select_raw_data, select_mintime, select_maxtime, select_error, (state, device_id, api) => api, (device_id, capabilities, raw_data, mintime, maxtime, error, api) => {
    if (error) {
        return Promise.reject(error);
    }
    if (capabilities != null &&
        capabilities.length > 0 &&
        raw_data != null &&
        mintime != null &&
        maxtime != null) {
        const onError = (error) => console.error(error);
        return postJob({
            type: JobType.track,
            capabilities,
            raw_data,
            time: {
                mintime: any2unixtime(mintime),
                maxtime: any2unixtime(maxtime),
            },
        }, onError, api);
    }
    return null;
});
export const async_select_tracking_gps = createSelector(async_select_track_and_icons, (state) => state.ui_settings.hide_gsm_points ?? false, (state) => state.ui_settings.accuracy_limit ?? null, (gps_and_icons, hide_gsm, accuracy_limit) => {
    if (gps_and_icons == null) {
        return Promise.resolve([]);
    }
    return gps_and_icons.then((result) => filterGPSPoints(result.gps, hide_gsm, accuracy_limit));
});
const empty_array = [];
export const async_select_tracking_gps_and_icons = createSelector(async_select_track_and_icons, (state) => state.ui_settings.hide_gsm_points ?? false, (state) => state.ui_settings.accuracy_limit ?? null, (gps_and_icons, hide_gsm, accuracy_limit) => {
    if (gps_and_icons == null) {
        return Promise.resolve([[], empty_array]);
    }
    return gps_and_icons.then((result) => [
        filterGPSPoints(result.gps, hide_gsm, accuracy_limit),
        result.icons ?? empty_array,
    ]);
});
export const device_last_loc_selector = createSelector((state, device_id) => selectDevice(state, device_id)?.last_gps?.[0], (last) => (last != null ? [last.x, last.y] : null));
export const async_select_ghost_location = createSelector(async_select_track_and_icons, (state, device_id) => selectDeviceTrack(state, device_id)?.ghost_at_time, (gps_and_icons, time) => gps_and_icons != null
    ? gps_and_icons.then((result) => ghostLocation(result.gps, time))
    : null);
// export const async_select_tracking_viewport_stats = createSelectorCreator(
//     memoize_by_first_arg2
// )(
export const async_select_tracking_viewport_stats = createSelector((state, device_id) => device_id, select_device_capabilities, select_raw_data, async_select_tracking_gps, selectDevice, select_confirmed_viewport, select_error, (state, device_id, api) => api, (device_id, capabilities, raw_data, async_gps, device, confirmed_viewport, error, api) => {
    if (error) {
        return Promise.reject(error);
    }
    if (capabilities != null &&
        capabilities.length > 0 &&
        raw_data != null &&
        confirmed_viewport != null &&
        device != null) {
        const onError = (error) => console.error(error);
        return async_gps.then((gps) => gps != undefined
            ? postJob({
                type: JobType.map_stats,
                capabilities,
                raw_data,
                time: {
                    mintime: any2unixtime(confirmed_viewport[0]),
                    maxtime: any2unixtime(confirmed_viewport[1]),
                },
                gps,
                device,
            }, onError, api)
            : {
                fuel: { consumption: null, avg_consumption: null },
                duration: 0,
                distance: 0,
            });
    }
    return null;
});
// export const async_select_trips = createSelectorCreator(memoize_by_first_arg2)(
export const async_select_trips = createSelector((state, device_id) => device_id, select_device_capabilities, select_raw_data, async_select_tracking_gps, selectDevice, select_error, (state, device_id, api) => api, (device_id, capabilities, raw_data, async_gps, device, error, api) => {
    if (error) {
        return Promise.reject(error);
    }
    if (capabilities != null &&
        capabilities.length > 0 &&
        raw_data != null &&
        device != null) {
        const onError = (error) => console.error(error);
        return async_gps.then((gps) => gps != undefined
            ? asyncGenerateTrips(capabilities, raw_data, gps, device, onError, api, postJob)
            : []);
    }
    return null;
});
const async_empty_trips = Promise.resolve({});
export const asyncSelectTripsIsVisible = createSelector(async_select_trips, (_, device_id, api, visible) => visible, (a_trips, visible) => {
    if (a_trips == null) {
        return async_empty_trips;
    }
    return a_trips.then((trips) => visible
        ? Object.fromEntries(trips.map((trip) => [
            trip.times.map((time) => time.getTime()).toString(),
            trip,
        ]))
        : async_empty_trips);
});
